import { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useAuth } from "../../../providers/auth";
import { storagePrefix } from "../../../utils/storage";
import {
  loginPaciente,
  recuperarContatos,
  recuperarEnviar,
  recuperarValidar,
  recuperarSenha,
  mensagemErro,
} from "../../../lib/authApi";

interface ILoginModal {
  show: boolean;
  onHide: () => void;
}

type View = "login" | "rec1" | "recMetodo" | "rec2" | "rec3";

const maskCpf = (v: string) =>
  v
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");

const LoginModal = ({ show, onHide }: ILoginModal) => {
  const { setPatientData } = useAuth();

  const [view, setView] = useState<View>("login");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [info, setInfo] = useState("");

  // login
  const [loginId, setLoginId] = useState("");
  const [senha, setSenha] = useState("");

  // recuperação
  const [recCpf, setRecCpf] = useState("");
  const [recCode, setRecCode] = useState("");
  const [recSenha, setRecSenha] = useState("");
  const [recConfirm, setRecConfirm] = useState("");
  const [recEmail, setRecEmail] = useState<string | null>(null); // máscara
  const [recPhone, setRecPhone] = useState<string | null>(null); // máscara

  const reset = () => {
    setView("login");
    setLoading(false);
    setErro("");
    setInfo("");
    setSenha("");
    setRecCode("");
    setRecSenha("");
    setRecConfirm("");
    setRecEmail(null);
    setRecPhone(null);
  };

  const fechar = () => {
    reset();
    onHide();
  };

  const entrar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");
    if (!loginId.trim() || !senha) return setErro("Informe seu CPF ou e-mail e a senha.");
    setLoading(true);
    try {
      const { token, userName, userId } = await loginPaciente(loginId.trim(), senha);
      const patientUser = { userName: userName ?? "paciente", userId: userId ?? 0 };
      localStorage.setItem(`${storagePrefix}token`, JSON.stringify(token));
      localStorage.setItem(`${storagePrefix}patientUser`, JSON.stringify(patientUser));
      setPatientData({ token, patientUser });
      fechar();
    } catch (err) {
      setErro(mensagemErro(err, "CPF/e-mail ou senha inválidos."));
    } finally {
      setLoading(false);
    }
  };

  const irRecuperar = () => {
    setErro("");
    setInfo("");
    setRecCpf(loginId.replace(/\D/g, "").length === 11 ? loginId : "");
    setView("rec1");
  };

  // Passo 1: busca os contatos mascarados e vai pra escolha de método.
  const recBuscar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");
    setInfo("");
    if (recCpf.replace(/\D/g, "").length !== 11) return setErro("Informe um CPF válido.");
    setLoading(true);
    try {
      const c = await recuperarContatos(recCpf.replace(/\D/g, ""));
      setRecEmail(c.email);
      setRecPhone(c.phone);
      setView("recMetodo");
    } catch (err) {
      setErro(mensagemErro(err, "Não encontramos uma conta com esse CPF."));
    } finally {
      setLoading(false);
    }
  };

  // Passo 2: envia o código pelo método escolhido.
  const recEnviar = async (metodo: "whatsapp" | "email") => {
    setErro("");
    setInfo("");
    setLoading(true);
    try {
      await recuperarEnviar(recCpf.replace(/\D/g, ""), metodo);
      setInfo(
        metodo === "email"
          ? `Enviamos um código para o e-mail ${recEmail ?? ""}.`
          : `Enviamos um código pelo WhatsApp ${recPhone ?? ""}.`
      );
      setView("rec2");
    } catch (err) {
      setErro(mensagemErro(err));
    } finally {
      setLoading(false);
    }
  };

  const recValidar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");
    if (recCode.trim().length < 4) return setErro("Digite o código recebido.");
    setLoading(true);
    try {
      await recuperarValidar(recCode.trim(), recCpf.replace(/\D/g, ""));
      setInfo("");
      setView("rec3");
    } catch (err) {
      setErro(mensagemErro(err, "Código inválido. Verifique e tente novamente."));
    } finally {
      setLoading(false);
    }
  };

  const recDefinir = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");
    if (recSenha.length < 6) return setErro("A senha deve ter ao menos 6 caracteres.");
    if (recSenha !== recConfirm) return setErro("As senhas não conferem.");
    setLoading(true);
    try {
      await recuperarSenha(recCode.trim(), recCpf.replace(/\D/g, ""), recSenha);
      setLoginId(recCpf);
      setSenha("");
      setInfo("Senha alterada! Agora é só entrar.");
      setErro("");
      setView("login");
    } catch (err) {
      setErro(mensagemErro(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={fechar} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-5 fw-semibold">
          {view === "login" ? "Entrar" : "Recuperar senha"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {view === "login" && (
          <form onSubmit={entrar} noValidate>
            <p className="text-muted mb-3">Entre com seu CPF e senha.</p>
            <div className="mb-3">
              <label className="form-label">CPF ou e-mail</label>
              <input
                className="form-control"
                type="text"
                autoComplete="username"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                placeholder="CPF ou e-mail"
              />
            </div>
            <div className="mb-2">
              <label className="form-label">Senha</label>
              <input
                className="form-control"
                type="password"
                autoComplete="current-password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                placeholder="sua senha"
              />
            </div>
            {info && <p className="text-success small mb-2">{info}</p>}
            {erro && <p className="text-danger small mb-2">{erro}</p>}
            <button className="btn btn-primary w-100 fw-semibold" type="submit" disabled={loading}>
              {loading ? "Entrando…" : "Entrar"}
            </button>
            <button type="button" className="btn btn-link w-100 mt-2 p-0" onClick={irRecuperar}>
              Esqueci minha senha
            </button>
          </form>
        )}

        {view === "rec1" && (
          <form onSubmit={recBuscar} noValidate>
            <p className="text-muted mb-3">Digite seu CPF para recuperar a senha.</p>
            <div className="mb-2">
              <label className="form-label">CPF</label>
              <input
                className="form-control"
                type="tel"
                inputMode="numeric"
                value={recCpf}
                onChange={(e) => setRecCpf(maskCpf(e.target.value))}
                placeholder="000.000.000-00"
              />
            </div>
            {erro && <p className="text-danger small mb-2">{erro}</p>}
            <button className="btn btn-primary w-100 fw-semibold" type="submit" disabled={loading}>
              {loading ? "Buscando…" : "Continuar"}
            </button>
            <button type="button" className="btn btn-link w-100 mt-2 p-0" onClick={() => setView("login")}>
              Voltar
            </button>
          </form>
        )}

        {view === "recMetodo" && (
          <div>
            <p className="text-muted mb-3">Onde você quer receber o código de recuperação?</p>
            {recPhone && (
              <button
                type="button"
                className="btn btn-outline-primary w-100 text-start d-flex flex-column mb-2"
                onClick={() => recEnviar("whatsapp")}
                disabled={loading}
              >
                <span className="fw-semibold">WhatsApp</span>
                <span className="small text-muted">{recPhone}</span>
              </button>
            )}
            {recEmail && (
              <button
                type="button"
                className="btn btn-outline-primary w-100 text-start d-flex flex-column mb-2"
                onClick={() => recEnviar("email")}
                disabled={loading}
              >
                <span className="fw-semibold">E-mail</span>
                <span className="small text-muted">{recEmail}</span>
              </button>
            )}
            {!recPhone && !recEmail && (
              <p className="text-danger small mb-2">
                Não encontramos um telefone ou e-mail cadastrado para esse CPF.
              </p>
            )}
            {loading && <p className="text-muted small mb-2">Enviando…</p>}
            {erro && <p className="text-danger small mb-2">{erro}</p>}
            <button type="button" className="btn btn-link w-100 mt-1 p-0" onClick={() => setView("rec1")}>
              Voltar
            </button>
          </div>
        )}

        {view === "rec2" && (
          <form onSubmit={recValidar} noValidate>
            <p className="text-muted mb-3">{info || "Digite o código que enviamos."}</p>
            <div className="mb-2">
              <label className="form-label">Código</label>
              <input
                className="form-control"
                type="tel"
                inputMode="numeric"
                value={recCode}
                onChange={(e) => setRecCode(e.target.value)}
                placeholder="código recebido"
              />
            </div>
            {erro && <p className="text-danger small mb-2">{erro}</p>}
            <button className="btn btn-primary w-100 fw-semibold" type="submit" disabled={loading}>
              {loading ? "Validando…" : "Validar código"}
            </button>
            <button type="button" className="btn btn-link w-100 mt-2 p-0" onClick={() => { setErro(""); setRecCode(""); setView("recMetodo"); }}>
              Não recebi — escolher outro método
            </button>
          </form>
        )}

        {view === "rec3" && (
          <form onSubmit={recDefinir} noValidate>
            <p className="text-muted mb-3">Crie uma nova senha.</p>
            <div className="mb-2">
              <label className="form-label">Nova senha</label>
              <input
                className="form-control"
                type="password"
                autoComplete="new-password"
                value={recSenha}
                onChange={(e) => setRecSenha(e.target.value)}
                placeholder="mínimo 6 caracteres"
              />
            </div>
            <div className="mb-2">
              <label className="form-label">Confirme a senha</label>
              <input
                className="form-control"
                type="password"
                autoComplete="new-password"
                value={recConfirm}
                onChange={(e) => setRecConfirm(e.target.value)}
                placeholder="repita a senha"
              />
            </div>
            {erro && <p className="text-danger small mb-2">{erro}</p>}
            <button className="btn btn-primary w-100 fw-semibold" type="submit" disabled={loading}>
              {loading ? "Salvando…" : "Salvar nova senha"}
            </button>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default LoginModal;
